import React, { useCallback, useEffect, useState, ReactNode } from "react";

// TS Particles
import { Engine, Options, RecursivePartial } from "tsparticles-engine";
import { loadSlim } from "tsparticles-slim";
import Particles from "react-particles";

import { useDarkMode } from "@hooks/dark-mode";
import { useTranslation } from "react-i18next";
import LogoCarousel from "@components/LogoCarousel";


function scrollToSection(id: string) {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
}

// Fade-in animation component with TypeScript types
interface FadeInSectionProps {
  children: ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
}

const FadeInSection: React.FC<FadeInSectionProps> = ({
  children,
  delay = 0,
  direction = "up"
}) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      });
    });

    const currentRef = domRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const getTransform = () => {
    switch (direction) {
      case "up": return "translateY(30px)";
      case "down": return "translateY(-30px)";
      case "left": return "translateX(30px)";
      case "right": return "translateX(-30px)";
      default: return "translateY(30px)";
    }
  };

  return (
    <div
      ref={domRef}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translate(0)" : getTransform(),
        transition: `opacity 0.8s ease-out, transform 1s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};


// The First View Of The Application
const Main = () => {
  // Starting Up The Particles
  const particlesInit = useCallback(async (engine: Engine) => {
    await loadSlim(engine);
  }, []);

  // Dark Mode
  const darkMode = useDarkMode();
  // Translation hook
  const { t } = useTranslation();

  // Text colors based on dark mode
  const textColor = darkMode.dark ? '#ffffff' : '#111827';
  const subtitleColor = darkMode.dark ? '#ffffff' : '#1f2937';
  const bgColor = darkMode.dark ? "#28282B" : "#F0F0F0";

  // The TS Particles Options
  const options: RecursivePartial<Options> = {
    background: {
      color: {
        value: bgColor
      }
    },
    fpsLimit: 120,
    interactivity: {
      events: {
        onClick: {
          enable: true,
          mode: "push"
        },
        onHover: {
          enable: true,
          mode: "repulse"
        },
        resize: {
          enable: true
        }
      },
      modes: {
        push: {
          quantity: 4
        },
        repulse: {
          distance: 100,
          duration: 0.5
        }
      }
    },
    fullScreen: {
      enable: false
    },
    particles: {
      color: {
        value: darkMode.dark ? "#80ABF9" : "#1673FF"
      },
      links: {
        color: darkMode.dark ? "#80ABF9" : "#1673FF",
        distance: 120,
        enable: true,
        opacity: 1,
        width: 2
      },
      move: {
        direction: "none",
        enable: true,
        outModes: {
          default: "bounce"
        },
        random: true,
        speed: 2.5,
        straight: false
      },
      number: {
        density: {
          enable: true,
          area: 800
        },
        value: 100
      },
      opacity: {
        value: 0.5
      },
      shape: {
        type: "square"
      },
      size: {
        value: { min: 1, max: 5 }
      }
    },
    detectRetina: true
  };

  return (
    <>
      {/* Main Section */}
      <section
        className="relative w-full flex flex-col justify-start items-center"
        style={{
          height: '100vh',
          backgroundColor: bgColor,
        }}
      >
        {/* Particles background */}
        <Particles
          id="tsparticles"
          options={options}
          init={particlesInit}
          className="z-0 absolute inset-0"
        />

        {/* Main content - Updated layout using Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 items-center max-w-screen-xl px-4 mx-auto z-20 w-full h-full">
          {/* Left Column: Text Content */}
          <div className="text-left max-w-2xl">
            {/* Main Title */}
            <FadeInSection delay={0.1}>
              <h1
                className="mb-4 text-4xl font-extrabold tracking-tight leading-none md:text-6xl xl:text-7xl"
                style={{ color: textColor }}
              >
                {t('home.main.title.get')} {" "}
                <span
                  className="transition-colors duration-300"
                  style={{
                    color: darkMode.dark ? "#80ABF9" : "#1673FF",
                  }}
                >
                  {t('home.main.title.actionable')}
                </span>
                <br />
                <span style={{ color: textColor }}>
                  {t('home.main.title.insights')}
                </span>
              </h1>
            </FadeInSection>

            <FadeInSection delay={0.3}>
              <p
                className="mb-8 font-normal md:text-lg lg:text-xl"
                style={{ color: subtitleColor }}
              >
                {t('home.main.subtitle')}
              </p>
            </FadeInSection>

            <FadeInSection delay={0.5}>
              <div className="flex items-center space-x-4">
                <button
                  className="inline-flex cursor-pointer transition-all items-center justify-center px-6 py-3 text-base font-medium text-center text-white rounded-lg focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-900"
                  onClick={() => window.location.href = "/contact-sales"}
                  style={{
                    backgroundColor: darkMode.dark ? "#80ABF9" : "#1673FF",
                    transition: "background-color 0.3s ease"
                  }}
                  onMouseOver={(event) => {
                    event.currentTarget.style.backgroundColor = darkMode.dark ? "#145CD9" : "#6B92D9";
                  }}
                  onMouseOut={(event) => {
                    event.currentTarget.style.backgroundColor = darkMode.dark ? "#1673FF" : "#80ABF9";
                  }}
                >
                  {t('home.main.getStartedButton')}
                  <svg className="w-5 h-5 ml-2 -mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                </button>
                <button
                  className="inline-flex cursor-pointer transition-all items-center justify-center text-base font-medium text-center focus:outline-none"
                  style={{ color: textColor }}
                  onClick={() => scrollToSection('solutions')}
                >
                  {t('home.main.learnMoreButton')}
                </button>
              </div>
            </FadeInSection>
          </div>

          {/* Right Column: Can be used for an image or graphic later */}
          <div></div>
        </div>
      </section>

      {/* Logo Carousel Section */}
      <section
        className="w-full py-6 bg-secBg"
      >
        <LogoCarousel />
      </section>
    </>
  );
};

// Exporting
export default Main;
